<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.adminCP.vehicle_category_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="visibleAddVehicleCategory">
                {{ $t('labels.addVehicleCategory') }}
            </hts-form-button>
        </div>
        <div class="body">
            <hts-data-table
                ref="dataTables"
                :headers="vehiclesTypeHeaders"
                :server="apiListVehiclesType"
                :use-archive="useVehicleArchive"
                :use-restore="useVehicleRestore"
                use-selection
                store-name="vehicleDataTable"
                store-key="VehicleCategory" />
        </div>
        <add-category
            ref="showDrawerAddVehicleCategory"
            :default-value="defaultValue"
            @refresh-tables="refreshTables()" />
        <edit-category
            ref="showDrawerEditVehicleCategory"
            :info-type-vehicle="typeVehicleIsSelectedForEdit"
            @refresh-tables="refreshTables()" />
        <hts-modal
            :show="showPopupDeleteVehicleType"
            :title="$t('labels.deleteVehicleCategory')"
            type="confirm"
            :content="$t('labels.confirmdeleteVehicleCategory')"
            @hide="closePopupDeleteVehicleCategory"
            @confirm="() => actionDeleteVehicleCategory()" />
        <hts-modal
            :show="openPopupAddNewCategory"
            :title="$t('labels.warningCreateCategoryVehicle')"
            :content="$t('labels.contentWarningCreateCategoryVehicle')"
            @hide="() => (openPopupAddNewCategory = false)" />
    </div>
</template>

<script lang="ts" setup>
import AddCategory from './ignore/add.vue'
import EditCategory from './ignore/edit.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const { $formatters } = useNuxtApp()

const dataTables = ref()
const showPopupDeleteVehicleType = ref(false)
const vehicleIsSelectedForDelete = ref<{ [key: string]: any }>({})
const apiListVehiclesType = $urlAdmin.vehicle.category['get-list']
const showDrawerAddVehicleCategory: any = ref(false)
const showDrawerEditVehicleCategory: any = ref(false)
const typeVehicleIsSelectedForEdit = reactive({})
const defaultValue = ref<number | null>(0)
const defaultName = ref('')

const handleEditVehicleCategory = (vehicleCategory: any) => {
  if (!vehicleCategory) {
    return null
  }
  Object.assign(typeVehicleIsSelectedForEdit, { ...vehicleCategory })
  if (showDrawerEditVehicleCategory.value) {
    showDrawerEditVehicleCategory.value.openEditCategory()
  }
}
const handleDeleteVehicleCategory = (item: any) => {
  if (!item) {
    return null
  }
  if (item && item._id) {
    vehicleIsSelectedForDelete.value = {
      selectedItems: [item._id]
    }
  } else {
    vehicleIsSelectedForDelete.value = item
  }
  showPopupDeleteVehicleType.value = true
}
const vehiclesTypeHeaders = [
  {
    field: 'name',
    label: 'name',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.vehicleModule.category.categoryName'),
    sortable: true
  },
  {
    field: 'from',
    label: 'from',
    headerName: $t('labels.fromKg'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.from)
    }
  },
  {
    field: 'to',
    label: 'to',
    headerName: $t('labels.comeFirstKg'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.to === 'number') {
        return $formatters.formatCurrency(params.data.to)
      }
      return '-'
    }
  },
  {
    field: 'wheelNumber',
    label: 'wheelNumber',
    headerName: $t('labels.vehicleModule.category.wheelNumber'),
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.wheelNumber === 'number') {
        return $formatters.formatCurrency(params.data.wheelNumber)
      }
      return '-'
    },
    sortable: true
  },
  {
    field: 'priceTires',
    label: 'priceTires',
    headerName: $t('labels.vehicleModule.category.priceTires'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.priceTires === 'number') {
        return $formatters.formatCurrency(params.data.priceTires)
      }
      return '-'
    }
  },
  {
    field: 'depreciationTires',
    label: 'depreciationTires',
    headerName: $t('labels.vehicleModule.category.depreciationTires'),

    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.depreciationTires === 'number') {
        return $formatters.formatCurrency(params.data.depreciationTires)
      }
      return '-'
    }
  },
  {
    field: 'repairingPrice',
    label: 'repairingPrice',
    headerName: $t('labels.vehicleModule.repairingPrice'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.repairingPrice === 'number') {
        return $formatters.formatCurrency(params.data.repairingPrice)
      }
      return '-'
    }
  },
  {
    field: 'liquidationValue',
    label: 'liquidationValue',
    headerName: $t('labels.liquidationValue'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.liquidationValue === 'number') {
        return $formatters.formatCurrency(params.data.liquidationValue)
      }
      return '-'
    }
  },
  {
    field: 'oilMaterialAssumption',
    label: 'oilMaterialAssumption',
    headerName: $t('labels.oilMaterialAssumption'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.oilMaterialAssumption === 'number') {
        return $formatters.formatCurrency(params.data.oilMaterialAssumption)
      }
      return '-'
    }
  },
  {
    field: 'insuranceValue',
    label: 'insuranceValue',
    headerName: $t('labels.insuranceValueAmountYear'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.insuranceValue === 'number') {
        return $formatters.formatCurrency(params.data.insuranceValue)
      }
      return '-'
    }
  },
  {
    field: 'depreciationRate',
    label: 'depreciationRate',
    headerName: $t('labels.depreciationRateAmountYear'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (typeof params.data.depreciationRate === 'number') {
        return $formatters.formatCurrency(params.data.depreciationRate)
      }
      return '-'
    }
  },
  {
    field: 'description',
    label: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'VehicleCategory',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.vehicle_category_edit),
          click: handleEditVehicleCategory
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          isArchive: true,
          permissions: checkPermission(layoutPermissions.adminCP.vehicle_category_delete),
          click: handleDeleteVehicleCategory
        }
      ]
    }
  }
]

const actionDeleteVehicleCategory = () => {
  const api = $urlAdmin.vehicle.category.delete
  $api(api, { body: vehicleIsSelectedForDelete.value }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      showPopupDeleteVehicleType.value = false
      process.client && $toast().success($t('labels.deleteTypeVehicleSuccess'))
      refreshTables()
    }
  })
}

const closePopupDeleteVehicleCategory = () => {
  showPopupDeleteVehicleType.value = false
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
    findAllVehicleCategory()
  }
}

const openPopupAddNewCategory = ref(false)

const visibleAddVehicleCategory = () => {
  if (typeof defaultValue.value !== 'number') {
    openPopupAddNewCategory.value = true
  } else {
    handleAddVehicleCategory()
  }
}

const handleAddVehicleCategory = () => {
  if (showDrawerAddVehicleCategory.value) {
    showDrawerAddVehicleCategory.value.openAddCategory()
  }
}
const findAllVehicleCategory = () => {
  const api = $urlAdmin.vehicle.category.getAll
  $api(api).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      const vehicleCategoryList = resData.data
      defaultName.value = vehicleCategoryList[vehicleCategoryList.length - 1] ? vehicleCategoryList[vehicleCategoryList.length - 1].name : ''
      defaultValue.value = vehicleCategoryList[vehicleCategoryList.length - 1] ? vehicleCategoryList[vehicleCategoryList.length - 1].to : 0
    }
  })
}

const useVehicleArchive = computed(() => {
  return {
    api: $urlAdmin.vehicle.category.archive,
    permissions: checkPermission(layoutPermissions.adminCP.vehicle_category_archive)
  }
})
const useVehicleRestore = computed(() => {
  return {
    api: $urlAdmin.vehicle.category.restore,
    permissions: true
  }
})
onMounted(() => {
  findAllVehicleCategory()
})
</script>
